<div [class]="embedded ? '' : 'rounded-lg border border-border bg-surface shadow-card p-5'">
  <h2 [class]="embedded ? 'text-label uppercase text-ink-muted' : 'text-card-title text-ink-primary'">Attachments</h2>

  @if (loading()) {
    <p class="mt-3 text-[13px] text-ink-muted">Loading files…</p>
  } @else if (items().length) {
    <div [class]="embedded ? 'mt-3 grid grid-cols-1 gap-3' : 'mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2'">
      @for (item of items(); track item.id) {
        <div>
          <div class="flex items-center gap-3 rounded-md border border-border-subtle bg-surface-canvas px-3 py-2">
            <span class="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold" [class]="kind(item).classes">
              {{ kind(item).label }}
            </span>
            <button
              type="button"
              (click)="download(item)"
              class="min-w-0 flex-1 truncate text-left text-[13px] text-ink-primary hover:text-action hover:underline"
              [title]="'Download ' + item.name"
            >
              {{ item.name }}
            </button>
            <div class="shrink-0 text-right text-[11px] leading-tight text-ink-muted">
              <p>{{ item.uploadedBy || '—' }}</p>
              <p>{{ item.uploadedAt | date: 'MMM d, y h:mm a' }} · {{ formatSize(item.size) }}</p>
            </div>
            @if (canEdit) {
              <button
                type="button"
                (click)="remove(item)"
                class="shrink-0 text-status-danger/80 hover:text-status-danger transition-colors"
                [attr.aria-label]="'Delete ' + item.name"
              >
                <pp-icon name="trash" [size]="15"></pp-icon>
              </button>
            }
          </div>

          <div class="mt-1 flex items-center gap-2 pl-1 text-[12px]">
            @if (!canEdit) {
              @if (item.validUntil) {
                <span [class]="isExpired(item) ? 'text-status-danger' : 'text-ink-secondary'">
                  {{ isExpired(item) ? 'Expired' : 'Valid until' }} {{ item.validUntil | date: 'MMM d, y' }}
                </span>
              }
            } @else if (editingValidUntilId() === item.id) {
              <input
                #validUntilInput
                type="date"
                class="rounded border border-border px-1.5 py-0.5 text-[12px]"
                [value]="item.validUntil ?? ''"
                (change)="saveValidUntil(item, validUntilInput.value)"
              />
              @if (item.validUntil) {
                <button type="button" (click)="saveValidUntil(item, '')" class="text-ink-muted hover:underline">Clear</button>
              }
              <button type="button" (click)="editingValidUntilId.set(null)" class="text-ink-muted hover:underline">Cancel</button>
            } @else if (item.validUntil) {
              <button
                type="button"
                (click)="editingValidUntilId.set(item.id)"
                class="hover:underline"
                [class]="isExpired(item) ? 'text-status-danger' : 'text-ink-secondary'"
              >
                {{ isExpired(item) ? 'Expired' : 'Valid until' }} {{ item.validUntil | date: 'MMM d, y' }}
              </button>
            } @else {
              <button type="button" (click)="editingValidUntilId.set(item.id)" class="text-action hover:underline">
                + Add valid until date
              </button>
            }
          </div>
        </div>
      }
    </div>
  }

  @if (!loading() && !items().length && !canEdit) {
    <p class="mt-3 text-[13px] text-ink-muted">No files attached.</p>
  }

  <!-- Drop zone (only for users who can change the record) -->
  @if (canEdit) {
  <div
    class="mt-3 flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors"
    [class]="dragOver() ? 'border-action bg-action-light' : 'border-border'"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
  >
    <svg class="h-6 w-6 text-ink-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M4 16.5v2A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5v-2" />
      <path d="M12 15V4" />
      <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
    </svg>
    @if (uploading()) {
      <p class="text-[13px] text-ink-secondary">Uploading…</p>
    } @else {
      <p class="text-[13px] text-ink-primary">Drag and drop files here</p>
      <p class="text-[12px] text-ink-muted">or</p>
      <button type="button" (click)="fileInput.click()" class="text-[13px] font-medium text-action hover:underline">
        Browse files
      </button>
      <p class="mt-1 text-[11px] text-ink-muted">Images, PDF, Excel or Word · up to 10 MB each</p>
    }
    <input #fileInput type="file" multiple class="hidden" [accept]="accept" (change)="onBrowse(fileInput)" />
  </div>
  }

  @if (errors().length) {
    <ul class="mt-2 flex flex-col gap-0.5 text-[12px] text-status-danger" role="alert">
      @for (error of errors(); track $index) {
        <li>{{ error }}</li>
      }
    </ul>
  }
</div>
